import React, { useState, useRef, useEffect } from "react";
import Stepper from "../stepper/Stepper";
import UserInfoForm from "./UserInfoForm";
import LocationInfoForm, { isOptionalContentMissing } from "./LocationInfoForm";
import CarTypeForm from "./CarTypeForm";
import PriceEstimationDisplay, { type PriceEstimationDisplayProps } from "./PriceEstimationDisplay";
import OrderForm from "./OrderForm";
import { useAppDispatch, useAppSelector, formatDates } from '../../app/hooks';
import strings, { selectLanguage } from '../../components/localize/localizeSlice';
import Login from "../login/Login";
import { useNewCaseCustomerOrderMutation, usePriceCalculationMutation, type NewCaseV2WithAdditonalRequirements, type CreateRegisteredCustomerDto, type UsernamePasswordLoginRequest, useRegisterCustomerMutation, useLoginMutation, useGetSelfQuery, type CreateCaseDto, useUpdateSelfMutation, type UpdateRegisteredCustomerDto } from "../../api/etApi";
import { updatePriceCalculationState, updateAlternateInvoiceData, orderFormComplete, login, setWaitingForMailConfirmation, setError, switchToLoginForm, switchToUserDataForm, goBackToPreviousForm, signInOrEnterUserDataFormComplete, pickupLocationFormComplete, destinationLocationFormComplete, carTypeFormComplete, updateUserInfoData, updatePickupLocationData, updateDestinationLocationData, updateCarTypeData, updateCustomerNote, orderFormError, accountAlreadyExists, switchToPickupLocationForm, switchToDestinationLocationForm } from "./PriceCalculatorFormSlice";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import SummaryDisplay from "./SummaryDisplay";
import { useYesNoDialog } from "../yesNoDialog/UseYesNoDialog";
import {getCountryById} from '../../components/countryPicker/Countries';

interface PriceCalculatorFormProps {
  onLoadRecaptchaToken: () => Promise<string | null>;
}

const PriceCalculatorForm: React.FC<PriceCalculatorFormProps> = ({ onLoadRecaptchaToken }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const confirmDialog = useYesNoDialog(false);
  const [triggerPriceCalculation, { isLoading: isLoadingPriceCalculation }] = usePriceCalculationMutation();
  const [registerCustomer, { isLoading: isRegistering }] = useRegisterCustomerMutation();
  const [updateCustomerData, { isLoading: _ }] = useUpdateSelfMutation();
  const [loginCustomer] = useLoginMutation();
  const [sendOrder] = useNewCaseCustomerOrderMutation();
  const refreshEstimateCountRef = useRef(0);
  const { data, error, refetch: refretchSelfInfo } = useGetSelfQuery();
  useAppSelector(selectLanguage);
  const dispatch = useAppDispatch();
  const {priceCalculationState, customerNote, alternateInvoiceData, userInfoData, pickupLocationData, destinationLocationData, carTypeData, formState, accountState, signInOrEnterUserDataFormState } = useAppSelector((state) => state.priceCalculator);
  useAppSelector(selectLanguage);
  const steps = [
    strings.form.pickupLocation.title,
    strings.form.destinationLocation.title,
    strings.form.vehicleDetails.title,
    strings.form.contactData.title,
    strings.form.orderSummary.title,
  ];

  useEffect(() => {
    if (data && !error && accountState !== 'loggedIn') {
      dispatch(login());
    }
  }, [data, error]);

  useEffect(() => {
    switch (formState) {
      case 'signInOrEnterUserDataFormVisible':
        setCurrentStep(3);
        break;
      case 'pickupLocationFormVisible':
        setCurrentStep(0);
        break;
      case 'destinationLocationFormVisible':
        setCurrentStep(1);
        break;
      case 'carTypeFormVisible':
        setCurrentStep(2);
        break;
      case 'orderFormVisible':
        setCurrentStep(4);
        break;
      case 'orderError':
      case 'orderComplete':
        setCurrentStep(5);
        break;
    }
  }, [formState]);

  const formatDateTime = (dateTime: string): string => {
    const date = new Date(dateTime);
    const pad = (num: number) => String(num).padStart(2, "0");
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(0)}:${pad(0)}:${pad(0)}`;
  };

  const createNewCaseDTO = (): NewCaseV2WithAdditonalRequirements => {
    const Res: NewCaseV2WithAdditonalRequirements = {
      General: {
        CaseType: 230,
        Branch: window.location.hostname === "cuno.et360.de" ? 3 : 1,
        Description: "",
      },
      Customer: {
        Name: `${userInfoData.firstName} ${userInfoData.lastName}`,
        Telephone: userInfoData.phone,
        Mobile: userInfoData.phone,
      },
      StartLocation: {
        Name: pickupLocationData.companyName || pickupLocationData.contactPerson,
        Country: pickupLocationData.country,
        City: pickupLocationData.city,
        Street: pickupLocationData.streetNumber,
        Zipcode: pickupLocationData.zip,
        Date: formatDateTime(pickupLocationData.date),
        Latitude: -1,
        Longitude: -1,
        ContactPerson: pickupLocationData.contactPerson,
        Time: (pickupLocationData.startHour[0] && pickupLocationData.startHour[1])
          ? formatDates(new Date(pickupLocationData.startHour[0] as string), new Date(pickupLocationData.startHour[1] as string))
          : "",
        Email: pickupLocationData.email,
        Telephone: pickupLocationData.telephone,
        Mobile: pickupLocationData.mobile
      },
      Destination: {
        Name: destinationLocationData.companyName || destinationLocationData.contactPerson,
        Country: destinationLocationData.country,
        City: destinationLocationData.city,
        Street: destinationLocationData.streetNumber,
        Zipcode: destinationLocationData.zip,
        Date: formatDateTime(destinationLocationData.date),
        Latitude: -1,
        Longitude: -1,
        ContactPerson: destinationLocationData.contactPerson,
        Time:
          (destinationLocationData.startHour[0] && destinationLocationData.startHour[1])
            ? formatDates(new Date(destinationLocationData.startHour[0] as string), new Date(destinationLocationData.startHour[1] as string))
            : "",
        Email: destinationLocationData.email,
        Telephone: destinationLocationData.telephone,
        Mobile: destinationLocationData.mobile
      },
      Vehicle: {
        LicensePlate: carTypeData.licensePlate,
        BrandModel: `${carTypeData.make} ${carTypeData.model}`,
        Color: "",
        Weight: parseInt(carTypeData.weight),
        VehicleType: carTypeData.carType === "LKW7.5" ? 4 : carTypeData.carType === "Anhänger750.5" ? 6 : parseInt(carTypeData.carType),
        ChassisNumber: carTypeData.chassisNumber,
        Registered: carTypeData.carRegistered,
        TireType: carTypeData.tires,
      },
      Requirements: carTypeData.specialOptions,
    };

    return Res;
  }

  useEffect(() => {
    if (currentStep === 2) {
      submitPriceCalculation();
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 2) {
      submitPriceCalculation();
    }
  }, [carTypeData.carRegistered, carTypeData.carType, carTypeData.specialOptions, carTypeData.weight]);

  const submitPriceCalculation = async () => {
    const newCaseData: NewCaseV2WithAdditonalRequirements = createNewCaseDTO();

    if (newCaseData.Vehicle?.VehicleType !== 2 && newCaseData.Vehicle?.VehicleType !== 11) {
      dispatch(updatePriceCalculationState('requestOnly'));
      return;
    }

    // Increment the refreshEstimateCount to track this request
    const currentRequestVersion = refreshEstimateCountRef.current + 1;
    refreshEstimateCountRef.current = currentRequestVersion;

    try {
      const response = await triggerPriceCalculation({ newCaseV2WithAdditonalRequirements: newCaseData }).unwrap();

      // Only update if this is the latest request
      if (refreshEstimateCountRef.current === currentRequestVersion) {
        dispatch(updatePriceCalculationState(response as PriceEstimationDisplayProps[]));
      }
    } catch (error) {
      // Only set to 'requestOnly' if this is the latest request
      if (refreshEstimateCountRef.current === currentRequestVersion) {
        dispatch(updatePriceCalculationState('requestOnly'));
      }
      console.error("Error during price calculation:", error);
    }
  };

  const sendNewCaseToBackend = async () => {
    const newCaseData: NewCaseV2WithAdditonalRequirements = createNewCaseDTO();
    const alternateStreet = alternateInvoiceData?.streetNumber ? alternateInvoiceData?.streetNumber + ", " + alternateInvoiceData?.zip + " " + alternateInvoiceData?.city + ", " + alternateInvoiceData?.country : userInfoData.address;
    const createDTO: CreateCaseDto = {
      General: newCaseData.General,
      StartLocation: newCaseData.StartLocation,
      Destination: newCaseData.Destination,
      Vehicle: newCaseData.Vehicle,
      Requirements: newCaseData.Requirements?.map(req => req === "RKZ" ? "AUF_ZZ_ROTE_KENNZEICHEN" : req === "AW" ? "AUF_ZZ_FHZ_WASCHEN" : req === "FEW" ? "AUF_ZZ_EINWEISUNG" : (() => { throw new Error("Unknown requirement") })()) || [],
      AlternateInvoiceRecipientName: alternateInvoiceData?.name || userInfoData.firstName + " " + userInfoData.lastName,
      ALternateInvoiceRecipientAddress: alternateStreet,
      CustomerNote: " Kundennotiz: " + customerNote,
      IsInquiry: Array.isArray(priceCalculationState) ? false : true,
    }

    if (createDTO?.General && createDTO?.Requirements) {
      createDTO.General.CaseType = createDTO.Requirements.includes("AUF_ZZ_ROTE_KENNZEICHEN") ? 222 : 230;
    }

    try {
      const result = await sendOrder({ createCaseDto: createDTO });
      if (result?.data) {
        dispatch(orderFormComplete());
      } else {
        dispatch(orderFormError());
      }
    } catch (error) {
      console.log("Error during order submission:", error);
      dispatch(orderFormError());
    }
  }

  // Refs for form validation
  const userInfoFormRef = useRef<{ validate: () => boolean }>(null);
  const pickupLocationFormRef = useRef<{ validate: () => boolean }>(null);
  const destinationLocationFormRef = useRef<{ validate: () => boolean }>(null);
  const carTypeFormRef = useRef<{ validate: () => boolean }>(null);

  const validateCurrentForm = (): boolean => {
    if (currentStep === 3 && userInfoFormRef.current && accountState !== 'loggedIn') {
      return userInfoFormRef.current.validate();
    } else if (currentStep === 0 && pickupLocationFormRef.current) {
      return pickupLocationFormRef.current.validate();
    } else if (currentStep === 1 && destinationLocationFormRef.current) {
      return destinationLocationFormRef.current.validate();
    } else if (currentStep === 2 && carTypeFormRef.current) {
      return carTypeFormRef.current.validate();
    }
    return true;
  };

  const createAccount = async () => {
    if (accountState != 'userDataSet' && accountState != 'error') {
      return;
    }

    const createDTO: CreateRegisteredCustomerDto = {
      Name: userInfoData.firstName + " " + userInfoData.lastName,
      Email: userInfoData.email,
      Address: userInfoData.address,
      Password: userInfoData.password,
      City: userInfoData.city,
      Country: userInfoData.country,
      Zip: userInfoData.zipcode,
      Telephone: getCountryById(userInfoData.phonePrefixCountry)?.COU_VORWAHL + " " + userInfoData.phone,
    }

    try {
      const recaptchaToken = await onLoadRecaptchaToken() ?? undefined;
      var res = await registerCustomer({ createRegisteredCustomerDto: createDTO, "g-recaptcha-response": recaptchaToken });
      if (res.error) {
        if (
          res.error !== null &&
          "originalStatus" in res.error &&
          "data" in res.error &&
          typeof (res.error as { data?: unknown }).data === "string"
        ) {
          const errorData = (res.error as { data: string }).data;
          if (errorData.includes("User already exists")) {
            alert(strings.userAlreadyExists);
            dispatch(accountAlreadyExists());
            return;
          } else if (errorData.includes("Recaptcha challenge failed.")) {
            alert(strings.recaptchaFailed);
            dispatch(setError());
            return;
          }
        }

        throw res.error;
      }
      dispatch(setWaitingForMailConfirmation());
      checkIfAccountVerified();
    }
    catch (error) {
      dispatch(setError());
      alert(strings.unknownError);
      console.error("Error during account creation:", error);
      return;
    }
  }

  const checkIfAccountVerified = async () => {
    while (true) {
      try {
        const LoginDTO: UsernamePasswordLoginRequest = {
          Username: userInfoData.email,
          Password: userInfoData.password,
          SetTokensAsCookieInstead: true,
        };

        await loginCustomer({ usernamePasswordLoginRequest: LoginDTO }).unwrap();
        dispatch(login());
        refretchSelfInfo();
        break;
      } catch (err) {
        // Wait for 10 seconds before retrying
        await new Promise((resolve) => setTimeout(resolve, 10000));
      }
    }
  };

  const handleNext = () => {
    const isValid = validateCurrentForm();
    setIsButtonDisabled(!isValid);

    if (isValid) {
      switch (currentStep) {
        case 3:
          dispatch(signInOrEnterUserDataFormComplete());
          break;
        case 0:
          dispatch(pickupLocationFormComplete());
          break;
        case 1:
          dispatch(destinationLocationFormComplete());
          break;
        case 2:
          dispatch(carTypeFormComplete());
          break;
      }
    }
  };

  const handleFormChange = (isValid: boolean) => {
    setIsButtonDisabled(!isValid);
  };

  return (
    <div className="p-4 flex flex-col lg:flex-row lg:gap-6">
      {/* Left Section: Form and Stepper */}
      <div className="flex-1">
        {/* Stepper Component */}
        {currentStep < 5 &&
          <div className="flex justify-center mt-[-20px] mb-5">
            <Stepper steps={steps} currentStep={currentStep} completedSteps={[]} />
          </div>
        }

        {/* Yes/No Dialog */}
        {confirmDialog.dialog}

        {/* Conditional Rendering Based on Current Step */}
        <div className="mt-6">
          {currentStep === 3 && (
            <>
              {signInOrEnterUserDataFormState === 'loginFormVisible' ? (<h1 className="mb-3 font-bold text-2xl text-gray-900 dark:text-white">
                {strings.form.login}
              </h1>) :
                <>
                  <h1 className="mb-3 font-bold text-2xl text-gray-900 dark:text-white">
                    {strings.form.contactData.title}
                  </h1>
                  <p className="mb-5 dark:text-white">
                    {accountState !== 'loggedIn' ? strings.form.contactData.description : strings.form.contactData.descriptionLoggedIn}
                  </p>
                </>
              }

              {(() => {
                return signInOrEnterUserDataFormState === 'loginFormVisible' ? (
                  <>
                    <Login onLoginSuccess={() => { dispatch(login()); }} />
                    <button
                      type="button"
                      className="mt-4 px-4 py-2 w-full text-sm font-medium text-cuno-500 hover:text-white dark:text-white ring-cuno-500 ring-2 shadow-sm rounded-md bg-transparent hover:bg-cuno-500 dark:bg-blue-500 dark:hover:bg-blue-600"
                      onClick={() => dispatch(switchToUserDataForm())}
                    >
                      {strings.form.createAccount}
                    </button>
                  </>
                ) : (
                  <>
                    {
                      accountState !== 'loggedIn' && <button
                        type="button"
                        className="px-4 py-2 text-sm font-medium text-cuno-500 hover:text-white ring-cuno-500 dark:text-white ring-2 shadow-sm rounded-md bg-transparent hover:bg-cuno-500 dark:bg-blue-500 dark:hover:bg-blue-600"
                        onClick={() => dispatch(switchToLoginForm())}
                      >
                        {strings.form.haveAccount}
                      </button>
                    }
                    <UserInfoForm
                      ref={userInfoFormRef}
                      onFormChange={handleFormChange}
                      formData={userInfoData}
                      setFormData={(data) => {
                        dispatch(updateUserInfoData(data));
                      }}
                    />

                  </>
                );
              })()}
            </>
          )}
          {currentStep === 0 && (
            <>
              <h1 className="mb-3 font-bold text-2xl text-gray-900 dark:text-white">
                {strings.form.pickupLocation.title}
              </h1>
              <p className="mb-5 dark:text-white">
                {strings.form.pickupLocation.description}
              </p>
              <LocationInfoForm
                translations={strings.form.pickupLocation}
                minDate={() => {
                  const now = new Date();
                  now.setSeconds(0, 0);
                  let minDate = new Date(now.getTime());
                  switch(minDate.getDay()) {
                    case 6: //saturday
                      minDate.setDate(minDate.getDate() + 3); //next tuesday
                      break;
                    case 0: //sunday
                      minDate.setDate(minDate.getDate() + 2); //next tuesday
                      break;
                    case 5: //friday
                      minDate.setDate(minDate.getDate() + 4); //next tuesday
                      break;
                    case 4: //thursday
                      minDate.setDate(minDate.getDate() + 4); //next monday
                      break;
                    default:
                      minDate.setDate(minDate.getDate() + 2);
                      break; 
                  }
                  minDate.setHours(8, 0, 0, 0);
                  var res = minDate.toISOString().slice(0, 10);
                  return res;
                }}
                hideHours={hour => hour < 8 || hour > 18}
                ref={pickupLocationFormRef}
                onFormChange={handleFormChange}
                formData={pickupLocationData}
                setFormData={(data) => {
                  dispatch(updatePickupLocationData(data));
                }}
              />
            </>
          )}
          {currentStep === 1 && (
            <>
              <h1 className="mb-3 font-bold text-2xl text-gray-900 dark:text-white">
                {strings.form.destinationLocation.title}
              </h1>
              <p className="mb-5 dark:text-white">
                {strings.form.destinationLocation.description}
              </p>
              <LocationInfoForm
                translations={{...strings.form.destinationLocation, email: strings.form.pickupLocation.email, mobile: strings.form.pickupLocation.mobile, phone: strings.form.pickupLocation.phone, companyName: strings.form.pickupLocation.companyName, contactPerson: strings.form.pickupLocation.contactPerson}}
                minDate={() => {
                  return pickupLocationData.date;
                }}
                hideHours={hour => {
                  if (pickupLocationData.date === destinationLocationData.date) {
                    const startHour = pickupLocationData.startHour && pickupLocationData.startHour[0];
                    const minHour = startHour ? new Date(startHour).getHours() : 0;
                    return hour < 8 || hour > 18 || (startHour ? hour < minHour : false);
                  }

                  return hour < 8 || hour > 18;
                }}
                ref={destinationLocationFormRef}
                onFormChange={handleFormChange}
                formData={destinationLocationData}
                setFormData={(data) => {
                  dispatch(updateDestinationLocationData(data));
                }}
              />
            </>
          )}
          {currentStep === 2 && (
            <>
              <h1 className="mb-3 font-bold text-2xl text-gray-900 dark:text-white">
                {strings.form.vehicleDetails.title}
              </h1>
              <p className="mb-5 dark:text-white">
                {strings.form.vehicleDetails.description}
              </p>
              <CarTypeForm
                ref={carTypeFormRef}
                onFormChange={handleFormChange}
                formData={carTypeData}
                setFormData={(data) => {
                  dispatch(updateCarTypeData(data));
                }}
              />
            </>
          )}
          {currentStep === 4 && (
            <>
              <h1 className="mb-3 font-bold text-2xl text-gray-900 dark:text-white">
                {strings.form.orderSummary.title}
              </h1>
              <p className="mb-5 dark:text-white">
                {strings.form.orderSummary.description}
              </p>

              {Array.isArray(priceCalculationState) ? (
                <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                  <PriceEstimationDisplay positions={priceCalculationState} />
                </div>
              ) : (
                <div className="flex justify-center items-center mt-10">
                  <span className="text-2xl font-extrabold text-cuno-500 dark:text-blue-400">{strings.form.orderSummary.requestOnly}</span>
                </div>
              )}

              {/* Summary Display */}
              <div className="mb-6">
                <SummaryDisplay />
              </div>

              {accountState === 'loggedIn' && (

                <>
                  <OrderForm
                   onSubmit={async () => {
                    const updateDTO: UpdateRegisteredCustomerDto = {
                      Name: userInfoData.firstName + " " + userInfoData.lastName,
                      Address: userInfoData.address,
                      City: userInfoData.city,
                      Country: userInfoData.country,
                      Zip: userInfoData.zipcode,
                      Telephone: getCountryById(userInfoData.phonePrefixCountry)?.COU_VORWAHL + " " + userInfoData.phone,
                    }
                    if (isOptionalContentMissing(pickupLocationData) || isOptionalContentMissing(destinationLocationData)) {
                      const userConfirmed = await confirmDialog.confirm({
                        title: strings.dialog.missingContactDataDialog.title,
                        message: strings.dialog.missingContactDataDialog.message,
                        yesText: strings.dialog.missingContactDataDialog.yesText,
                        noText: strings.dialog.missingContactDataDialog.noText,
                        defaultButton: "yes"
                      });
                      if (userConfirmed && !isOptionalContentMissing(pickupLocationData)) {
                        dispatch(switchToDestinationLocationForm());
                        return;
                      }
                      else if (userConfirmed) {
                        dispatch(switchToPickupLocationForm());
                        return;
                      }
                    }

                    await updateCustomerData({ updateRegisteredCustomerDto: updateDTO });
                    sendNewCaseToBackend();
                  }} onBack={() => { dispatch(goBackToPreviousForm()); }} alternateInvoiceData={alternateInvoiceData}
                    setFormData={(data) => {
                      dispatch(updateAlternateInvoiceData(data));
                    }}
                    setCustomerNote={(note) => {
                      dispatch(updateCustomerNote(note));
                    }} />
                </>
              )}
              {((accountState === 'userDataSet' && !isRegistering) || accountState === 'error') && (
                <>
                  <button
                    type="button"
                    onClick={createAccount}
                    className="w-full  text-white hover:!bg-cuno-700 !dark:bg-blue-500 !dark:hover:bg-blue-600 px-6 mt-10 py-2 text-sm font-medium bg-cuno-500 ring-cuno-500 ring-2 shadow-sm focus:outline-2 focus:ring-2 focus:ring-cuno-900 focus:ring-offset-2"
                  >
                    {strings.form.orderSummary.createAccountNow}
                  </button>
                  <button
                    type="button"
                    onClick={() => dispatch(goBackToPreviousForm())}
                    className="w-full mt-4 px-6 py-2 text-sm font-medium ring-2 ring-gray-300  shadow-sm bg-gray-200 hover:bg-gray-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:bg-gray-500 dark:hover:bg-gray-600"
                  >
                    {strings.form.back}
                  </button>
                </>
              )}
              {accountState === 'userDataSet' && isRegistering && (
                <div className="flex justify-center items-center mt-20">
                  <div className="flex flex-col items-center mt-4">
                    <svg aria-hidden="true" className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-cuno-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                  </div>
                </div>
              )}
              {accountState === 'waitingForMailConfirmation' && (
                <div className="flex justify-center items-center mt-20">
                  <div className="flex flex-col items-center mt-4">
                    <svg aria-hidden="true" className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-cuno-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                    <span className="text-center mt-4">{strings.form.orderSummary.emailVerification}</span>
                  </div>
                </div>
              )}
            </>
          )}
          {currentStep === 5 && formState === 'orderComplete' && (
            <>
              <h1 className=" font-bold text-2xl text-gray-900 dark:text-white">
                {strings.successTitle}
              </h1>
              <div className="flex justify-center items-center">
                <DotLottieReact
                  src={`${import.meta.env.BASE_URL}Done.lottie`}
                  autoplay
                  className="w-60 h-60"
                  width={60}
                  height={60}
                />
                <p className="mb-5 dark:text-white">
                  { Array.isArray(priceCalculationState) ? strings.success : strings.successRequest }
                </p>
              </div>
            </>
          )}
          {currentStep === 5 && formState === 'orderError' && (
            <>
              <h1 className=" font-bold text-2xl text-gray-900 dark:text-white">
                {strings.errorTitle}
              </h1>
              <div className="flex justify-center items-center">
                <DotLottieReact
                  src={`${import.meta.env.BASE_URL}Error.lottie`}
                  autoplay
                  className="w-60 h-60"
                  width={60}
                  height={60}
                />
                <p className="mb-5 dark:text-white">
                  {strings.error}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 4 && (signInOrEnterUserDataFormState != 'loginFormVisible' || formState !== 'signInOrEnterUserDataFormVisible') && (
          <>
            <div className="mt-6 flex justify-between">
              {(currentStep > 0 ) ? (
                <button
                  type="button"
                  onClick={() => dispatch(goBackToPreviousForm())}
                  className="px-6 py-2 text-sm font-medium !text-white !bg-gray-600 rounded-md shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:bg-gray-500 dark:hover:bg-gray-600"
                >
                  {strings.form.back}
                </button>
              ) : (
                <div />
              )}
              <button
                type="button"
                onClick={handleNext}
                disabled={isButtonDisabled}
                className={`px-6 py-2 text-sm font-medium text-cuno-500 hover:text-white ring-cuno-500 ring-2 shadow-sm focus:outline-2 focus:ring-2 focus:ring-cuno-900 focus:ring-offset-2 ${isButtonDisabled
                  ? "!bg-gray-400 !text-gray-200 !cursor-not-allowed"
                  : "bg-transparent hover:!bg-cuno-500 !dark:bg-blue-500 !dark:hover:bg-blue-600"
                  }`}
              >
                {strings.form.continue}
              </button>
            </div>
          </>)}
      </div>

      {/* Right Section: Price Estimation */}
      {currentStep === 2 && (isLoadingPriceCalculation || priceCalculationState) && (
        <div className="hidden lg:block w-[40%] border-l border-gray-300 dark:border-gray-700 pl-6">
          {isLoadingPriceCalculation ? (
            <div className="flex justify-center items-center mt-40">
              <div role="status">
                <svg aria-hidden="true" className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-cuno-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                  <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                </svg>
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )
            :
            Array.isArray(priceCalculationState) ? (
              <PriceEstimationDisplay positions={priceCalculationState} />
            ) : (
              <div className="flex justify-center items-center mt-10">
                <span className="text-2xl font-extrabold text-cuno-500 dark:text-blue-400">{strings.form.orderSummary.requestOnly}</span>
              </div>
            )
          }
        </div>
      )}
    </div>
  );
};

export default PriceCalculatorForm;